import React, { useState } from "react";
import { Clapperboard, Eye, ImagePlus, LayoutGrid, PencilLine, Save } from "lucide-react";
import BannerUploader from "./BannerUploader";
import PromoCardsUploader from "./PromoCardsUploader";
import HighlightVideoEditor from "./HighlightVideoEditor";
import {
  getHighlightVideoValidationMessage,
  getPromoCardsValidationMessage,
} from "../../utils/storeDefaults";

const StoreEditorPanel = ({
  storeConfig,
  onChange,
  onSave,
  isSaving = false,
  hasUnsavedChanges = false,
}) => {
  const [highlightVideoValid, setHighlightVideoValid] = useState(true);

  const updateField = (field, value) => {
    onChange({ ...storeConfig, [field]: value });
  };

  const promoCardsError = getPromoCardsValidationMessage(storeConfig.promoCards);
  const highlightVideoError = getHighlightVideoValidationMessage(
    storeConfig.highlightVideo
  );
  const canSave =
    hasUnsavedChanges && !promoCardsError && !highlightVideoError && highlightVideoValid;

  return (
    <div className="space-y-6">
      <div className="p-5 bg-white border border-gray-100 rounded-3xl shadow-sm">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-rose-50 text-[#FF6C6C] shrink-0">
            <PencilLine className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Store story</h3>
            <p className="text-sm text-gray-500">
              Tell shoppers who you are. Changes reflect instantly in the preview.
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="block mb-1.5 text-sm font-medium text-gray-700">
              Tagline
            </label>
            <input
              type="text"
              value={storeConfig.tagline || ""}
              onChange={(event) => updateField("tagline", event.target.value)}
              placeholder="e.g. Fresh deals, every day"
              maxLength={80}
              className="w-full px-4 py-3 text-sm border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]"
            />
          </div>

          <div>
            <label className="block mb-1.5 text-sm font-medium text-gray-700">
              About your store
            </label>
            <textarea
              value={storeConfig.about || ""}
              onChange={(event) => updateField("about", event.target.value)}
              placeholder="Share what makes your brand special..."
              rows={4}
              maxLength={400}
              className="w-full px-4 py-3 text-sm border border-gray-200 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]"
            />
            <p className="mt-1 text-xs text-right text-gray-400">
              {(storeConfig.about || "").length}/400
            </p>
          </div>
        </div>
      </div>

      <div className="p-5 bg-white border border-gray-100 rounded-3xl shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-sky-50 text-sky-600 shrink-0">
            <ImagePlus className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Store banners</h3>
            <p className="text-sm text-gray-500">
              Hero images shoppers see first on your store page.
            </p>
          </div>
        </div>

        <BannerUploader
          banners={storeConfig.banners || []}
          onChange={(banners) => updateField("banners", banners)}
          disabled={isSaving}
        />
      </div>

      <div className="p-5 bg-white border border-gray-100 rounded-3xl shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-50 text-indigo-600 shrink-0">
            <LayoutGrid className="w-5 h-5" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-bold text-gray-900">Category highlights</h3>
              <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 rounded-full">
                Optional
              </span>
            </div>
            <p className="text-sm text-gray-500">
              Three promo cards in a row, shown below your banner and store info.
            </p>
          </div>
        </div>

        <PromoCardsUploader
          promoCards={storeConfig.promoCards || []}
          onChange={(promoCards) => updateField("promoCards", promoCards)}
          disabled={isSaving}
        />
      </div>

      <div className="p-5 bg-white border border-gray-100 rounded-3xl shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-sm shrink-0">
            <Clapperboard className="w-5 h-5" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-bold text-gray-900">Spotlight video</h3>
              <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 rounded-full">
                Optional
              </span>
            </div>
            <p className="text-sm text-gray-500">
              Embed a YouTube video below your category highlights — cinematic
              1697×602 frame on your live store.
            </p>
          </div>
        </div>

        <HighlightVideoEditor
          highlightVideo={storeConfig.highlightVideo || {}}
          onChange={(highlightVideo) => updateField("highlightVideo", highlightVideo)}
          onValidationChange={setHighlightVideoValid}
          disabled={isSaving}
        />
      </div>

      <div className="p-5 bg-white border border-gray-100 rounded-3xl shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 shrink-0">
            <Eye className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Visibility</h3>
            <p className="text-sm text-gray-500">
              Control whether your store is ready for customers on Dealboxx.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 rounded-2xl bg-gray-50">
          <div>
            <p className="text-sm font-semibold text-gray-800">Publish store</p>
            <p className="text-xs text-gray-500">
              When live, your store page will be visible to shoppers.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={Boolean(storeConfig.isPublished)}
            onClick={() => updateField("isPublished", !storeConfig.isPublished)}
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
              storeConfig.isPublished ? "bg-[#FF6C6C]" : "bg-gray-200"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                storeConfig.isPublished ? "translate-x-5" : "translate-x-0.5"
              } mt-0.5`}
            />
          </button>
        </div>

        <div className="flex items-center justify-between mt-3 p-4 rounded-2xl bg-gray-50">
          <div>
            <p className="text-sm font-semibold text-gray-800">Show contact bar</p>
            <p className="text-xs text-gray-500">
              Display phone, website, and social links on your store.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={storeConfig.showContactBar !== false}
            onClick={() => updateField("showContactBar", !storeConfig.showContactBar)}
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
              storeConfig.showContactBar !== false ? "bg-[#FF6C6C]" : "bg-gray-200"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                storeConfig.showContactBar !== false ? "translate-x-5" : "translate-x-0.5"
              } mt-0.5`}
            />
          </button>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 p-4 -mx-1 bg-gradient-to-t from-[#FAFAFA] via-[#FAFAFA] to-transparent">
        <button
          type="button"
          onClick={onSave}
          disabled={isSaving || !canSave}
          className="inline-flex items-center justify-center w-full gap-2 px-5 py-3.5 text-sm font-semibold text-white rounded-2xl bg-[#FF6C6C] shadow-lg shadow-rose-200 transition-all hover:bg-[#e65e5e] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          {isSaving ? "Saving..." : "Save store"}
        </button>
      </div>
    </div>
  );
};

export default StoreEditorPanel;
